import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

export default function PropertyDeleteButton({ property, onDelete }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Authentication error:', userError);
        throw new Error('Please log in to delete properties');
      }
      if (!user) {
        throw new Error('Please log in to delete properties');
      }
      
      console.log('=== DELETE DEBUG START ===');
      console.log('1. Supabase auth user:', user);
      console.log('2. Attempting to fetch property with ID:', property.id);
      
      // Get fresh property data from the database
      const { data: propertyData, error: fetchError } = await supabase
        .from('properties')
        .select('*')
        .eq('id', property.id)
        .single();
        
      if (fetchError) {
        console.error('Error fetching property:', fetchError);
        
        // Let's check what properties actually exist
        const { data: allProperties, error: allPropsError } = await supabase
          .from('properties')
          .select('id, title, owner_id')
          .limit(10);
          
        console.log('All properties in database:', allProperties);
        console.log('Error getting all properties:', allPropsError);
        
        throw new Error(`Property with ID ${property.id} not found. Available properties: ${allProperties?.map(p => p.id).join(', ') || 'none'}`);
      }
      
      console.log('3. Property data from DB:', propertyData);

      // Check if user is the owner by finding their user record in the custom database
      const { data: customUser, error: customUserError } = await supabase
        .from('users')
        .select('id, email')
        .eq('email', user.email)
        .single();
        
      console.log('3. Custom user lookup:');
      console.log('   - Query: email =', user.email);
      console.log('   - Result:', customUser);
      console.log('   - Error:', customUserError);
      
      if (customUserError) {
        console.error('Error finding user in custom database:', customUserError);
        
        // If user not found, let's check what users exist
        const { data: allUsers, error: allUsersError } = await supabase
          .from('users')
          .select('id, email')
          .limit(10);
          
        console.log('4. All users in database:', allUsers);
        console.log('   Error getting users:', allUsersError);
        
        throw new Error(`User not found in database. Searched for email: ${user.email}`);
      }

      // Check ownership: handle both old UUID-based and new integer-based owner_ids
      const isOwnerByInteger = propertyData.owner_id === customUser.id; // New properties (integer)
      const isOwnerByUUID = propertyData.owner_id === user.id; // Old properties (UUID)
      const isOwner = isOwnerByInteger || isOwnerByUUID;
      const isAdmin = customUser.id === 1; // Assuming admin has ID 1
      
      console.log('5. Authorization check:');
      console.log('   - Supabase user ID (UUID):', user.id);
      console.log('   - Custom user ID (integer):', customUser.id);
      console.log('   - Property owner_id:', propertyData.owner_id);
      console.log('   - Property owner_id type:', typeof propertyData.owner_id);
      console.log('   - Is owner by integer?', isOwnerByInteger);
      console.log('   - Is owner by UUID?', isOwnerByUUID);
      console.log('   - Final is owner?', isOwner);
      console.log('   - Is admin?', isAdmin);
      console.log('=== DELETE DEBUG END ===');
      
      if (!isOwner && !isAdmin) {
        throw new Error(`AUTHORIZATION FAILED - You are not authorized to delete this property.
        
Debug Info:
- Your Supabase UUID: ${user.id}
- Your Database ID: ${customUser.id}
- Property owner_id: ${propertyData.owner_id} (${typeof propertyData.owner_id})
- Integer match: ${isOwnerByInteger}
- UUID match: ${isOwnerByUUID}
- Admin check: ${isAdmin}`);
      }
      
      // Delete associated images from storage if they exist
      if (propertyData.images && propertyData.images.length > 0) {
        const filesToDelete = [];
        
        for (const imageUrl of propertyData.images) {
          try {
            // Handle both full URLs and relative paths
            let filePath = imageUrl;
            
            // If it's a full URL, extract the path after the storage path
            if (imageUrl.startsWith('http')) {
              const url = new URL(imageUrl);
              // The path will be something like "/storage/v1/object/public/bucket-name/userId/filename"
              const pathParts = url.pathname.split('/');
              // Get the parts after "public" in the path
              const publicIndex = pathParts.indexOf('public');
              if (publicIndex !== -1) {
                filePath = pathParts.slice(publicIndex + 1).join('/');
              } else {
                // If we can't find 'public', try to get the last parts
                filePath = pathParts.slice(-2).join('/');
              }
            }
            
            console.log('Attempting to delete file at path:', filePath);
            filesToDelete.push(filePath);
          } catch (err) {
            console.error('Error processing image URL:', imageUrl, err);
          }
        }
        
        if (filesToDelete.length > 0) {
          console.log('Files to delete:', filesToDelete);
          const { error: storageError } = await supabase.storage
            .from('property-images')
            .remove(filesToDelete);
            
          if (storageError) {
            console.error('Error deleting images:', storageError);
            // Don't throw here, we can still try to delete the property
          } else {
            console.log('Successfully deleted', filesToDelete.length, 'images');
          }
        }
      }
      
      // Now delete the property
      const { error: deleteError } = await supabase
        .from('properties')
        .delete()
        .eq('id', property.id);
        
      if (deleteError) throw deleteError;
      
      // Call the onDelete callback if provided
      if (onDelete) {
        onDelete(property.id);
      }
      
      handleClose();
      
      // Redirect to properties list after successful deletion
      navigate('/properties', { 
        replace: true,
        state: { message: `Property "${property.title}" has been successfully deleted.` }
      });
      
    } catch (error) {
      console.error('Error in handleDelete:', error);
      setError(error.message || 'Failed to delete property. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Delete Property">
        <IconButton 
          onClick={handleClickOpen}
          color="error"
          size="small"
          sx={{ ml: 1 }}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
      
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Delete Property</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this property? This action cannot be undone.
            {error && (
              <div style={{ color: 'red', marginTop: '10px' }}>
                {error}
              </div>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
            startIcon={<DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}